import { ChatRequestMode } from "@shaarp/common";
import {
  Building2Icon,
  type LucideIcon,
  RadarIcon,
  UserSearchIcon,
} from "lucide-react";

/**
 * Request modes, in the order they are offered. Each tells the assistant what
 * the message expects back — a list of prospects, one company, one contact
 * card — and never which tool to call: the API turns the mode into a block of
 * the interface note that names the matching families of the chat prompt.
 */
export const CHAT_REQUEST_MODES: readonly ChatRequestMode[] = [
  "prospecting",
  "company",
  "contact",
];

export const CHAT_REQUEST_MODE_ICONS: Record<ChatRequestMode, LucideIcon> = {
  prospecting: RadarIcon,
  company: Building2Icon,
  contact: UserSearchIcon,
};

/** Full label, where the mode is offered and on the message sent in it. */
export const CHAT_REQUEST_MODE_LABEL_KEYS: Record<ChatRequestMode, string> = {
  prospecting: "chat.input.modes.prospecting.label",
  company: "chat.input.modes.company.label",
  contact: "chat.input.modes.contact.label",
};

/** Short label, used by the inline prefix once the mode is armed. */
export const CHAT_REQUEST_MODE_CHIP_KEYS: Record<ChatRequestMode, string> = {
  prospecting: "chat.input.modes.prospecting.chip",
  company: "chat.input.modes.company.chip",
  contact: "chat.input.modes.contact.chip",
};

/** Composer placeholder, hinting at what the armed mode expects. */
const CHAT_REQUEST_MODE_PLACEHOLDER_KEYS: Record<ChatRequestMode, string> = {
  prospecting: "chat.input.modes.prospecting.placeholder",
  company: "chat.input.modes.company.placeholder",
  contact: "chat.input.modes.contact.placeholder",
};

/**
 * The composer placeholder of an armed mode.
 *
 * « Prospection » also finds people described by their role — a table of
 * contacts beside the conversation (#668) — where the chat offers that search
 * (`useIsPeopleSearchOffered`, the very flag that tells the API to register
 * it). Elsewhere — a company's chat, a team that does not see people — the
 * mode only lists companies, and the placeholder says so.
 */
export function getChatRequestModePlaceholderKey(
  mode: ChatRequestMode,
  isPeopleSearchOffered: boolean,
): string {
  return mode === "prospecting" && isPeopleSearchOffered
    ? "chat.input.modes.prospecting.placeholderWithPeople"
    : CHAT_REQUEST_MODE_PLACEHOLDER_KEYS[mode];
}

/** What each mode answers with, told before it is picked. */
const CHAT_REQUEST_MODE_DESCRIPTION_KEYS: Record<ChatRequestMode, string> = {
  prospecting: "chat.input.modes.prospecting.description",
  company: "chat.input.modes.company.description",
  contact: "chat.input.modes.contact.description",
};

/**
 * The help text of a mode, shown on its pill.
 *
 * « Un contact » and « Prospection » both read as the place for « une liste de
 * DAF » — one offers « un poste dans une entreprise », the other « les
 * contacts à trouver » — and a manual test on #430 picked the wrong one. The
 * help text says which one lists people: « Prospection », where the chat
 * offers the people search, while « Un contact » is one specific person.
 */
export function getChatRequestModeDescriptionKey(
  mode: ChatRequestMode,
  isPeopleSearchOffered: boolean,
): string {
  return mode === "prospecting" && isPeopleSearchOffered
    ? "chat.input.modes.prospecting.descriptionWithPeople"
    : CHAT_REQUEST_MODE_DESCRIPTION_KEYS[mode];
}
